import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import React from 'react';
import { AsChildContextProvider, useAsChild } from '../../contexts';
import { useRouterMutateOnClick } from '../../contexts/router-context/router-context';
import { type ExtendWithCommonProps } from '../../types';
import { Flex } from '../flex/flex';
import { Text, type TextProps } from '../text/text';
import { LinkIcon } from './link-icon';
import scss from './link.module.scss';

export type LinkProps = ExtendWithCommonProps<
  {
    /** Text variant. */
    variant?: Extract<
      TextProps['variant'],
      'link-100' | 'hyp-100' | 'hyp-200' | 'hyp-lgl'
    >;
    /** Link type. Use `<IconLink>` for 'icon'. */
    linkType?: 'text' | 'icon';
    /** If true, link is disabled. */
    disabled?: boolean;
    /** If true, link is inline. */
    inline?: boolean;
    asChild?: boolean;
    children: React.ReactNode;
  },
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>;

const Link = React.forwardRef<React.ElementRef<'a'>, LinkProps>(
  (
    {
      variant = 'link-100',
      linkType = 'text',
      children,
      className,
      disabled,
      inline,
      asChild,
      ...props
    },
    ref,
  ) => {
    const { hasAsChildParent } = useAsChild();

    const classes = classNames(
      scss.link,
      scss[linkType],
      {
        [scss.disabled]: disabled,
        [scss.inline]: inline,
      },
      className,
    );

    useRouterMutateOnClick(props);

    return (
      <AsChildContextProvider hasAsChildParent={asChild}>
        {hasAsChildParent ? (
          <a ref={ref} className={className} {...props}>
            {children}
          </a>
        ) : (
          <Text variant={variant} asChild>
            {asChild ? (
              <Flex gap={50} alignItems="center" asChild>
                <Slot className={classes} {...props}>
                  {children}
                </Slot>
              </Flex>
            ) : (
              <a
                ref={ref}
                className={hasAsChildParent ? className : classes}
                {...props}
              >
                <Flex gap={50} alignItems="center">
                  {children}
                </Flex>
              </a>
            )}
          </Text>
        )}
      </AsChildContextProvider>
    );
  },
);

/**
 * Link component. Use to navigate to a different page or section.
 *
 * Composed of:
 *  - `<Link.Icon>` to add an icon to the link.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-link--docs
 */
const LinkNamespace = Object.assign(Link, { Icon: LinkIcon });
export { LinkNamespace as Link };

Link.displayName = 'Link';
